
import type { UPSRunCreateRequest } from "../services/upsSizing";
import { downloadRunJson } from "../services/runExport";
import { RunTraceabilityPanel } from "../components/RunTraceabilityPanel";
import { SldLinkPanel } from "../components/SldLinkPanel";
import { SourceSizingSwitch } from "../components/SourceSizingSwitch";
import { StudyProjectLine } from "../components/StudyProjectLine";
import { UPSResultPanel } from "../components/UPSResultPanel";
import { UPSResultSummary } from "../components/UPSResultSummary";
import { UPSStudyForm } from "../components/UPSStudyForm";
import { UPSWarningPanel } from "../components/UPSWarningPanel";
import { useSldElements } from "../hooks/useSldElements";
import { useUPSSizing } from "../hooks/useUPSSizing";

function describeError(error: unknown): string {
  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }
  return "The UPS sizing study could not be completed.";
}

// Study-page layout (Master Prompt v2.1 section 19): collapsible inputs beside
// a result-first column - summary, warnings, traceability, then detail tables.
export function UPSSizingPage() {
  const { calculate, reset, result, run, error, isPending, isError } = useUPSSizing();
  // The active revision's single-line diagram, for the source picker (A28 (d)).
  const { elements: sldElements } = useSldElements();

  // Errors are surfaced through the hook state below, not thrown into the form.
  async function handleSubmit(payload: UPSRunCreateRequest): Promise<void> {
    try {
      await calculate(payload);
    } catch {
      // Intentionally empty: isError / error already describe the failure.
    }
  }

  return (
    <main data-ups-sizing-page>
      <SourceSizingSwitch />
      <header>
        <p>EOS-03 · UPS Sizing</p>
        <h1>UPS sizing</h1>
        <p>
          Size a UPS against the critical load, the UPS rating basis and the required runtime
          of the project design basis, using the validated KES Electrical OS calculation
          service. Battery sizing is not evaluated.
        </p>
      </header>

      <div data-study-layout>
        <section aria-labelledby="ups-study-inputs-heading">
          <details open data-study-inputs>
            <summary>
              <h2 id="ups-study-inputs-heading">Inputs</h2>
            </summary>
            <StudyProjectLine />
            <UPSStudyForm
              disabled={isPending}
              onSubmit={handleSubmit}
              sldElements={sldElements}
            />
          </details>
        </section>

        <section aria-labelledby="ups-study-results-heading" data-study-results>
          <h2 id="ups-study-results-heading">Results</h2>

          {isPending ? (
            <p role="status" data-calculation-state="pending">
              Calculating the UPS size…
            </p>
          ) : null}

          {isError ? (
            <p role="alert" data-calculation-state="error">
              {describeError(error)}
            </p>
          ) : null}

          {result ? (
            <div data-calculation-state="success">
              <UPSResultSummary result={result} />
              {run ? <SldLinkPanel link={run.sld_link} /> : null}
              <UPSWarningPanel warnings={result.warnings} />
              {run ? (
                <RunTraceabilityPanel
                  run={run}
                  onExport={() =>
                    downloadRunJson(
                      { run, result },
                      `${run.calculation_key}-rev${run.revision_number}.json`,
                    )
                  }
                />
              ) : null}
              <UPSResultPanel result={result} />
              <p>
                <button type="button" onClick={reset}>
                  Clear results
                </button>
              </p>
            </div>
          ) : null}

          {!isPending && !isError && !result ? (
            <p data-calculation-state="idle">Submit the inputs to size a UPS.</p>
          ) : null}
        </section>
      </div>

      <section aria-labelledby="ups-study-review-heading">
        <h2 id="ups-study-review-heading">Engineering review required</h2>
        <p>
          Calculation results are engineering evidence, not an automatic compliance
          declaration. Confirm the governing project basis, the critical load, the required
          runtime, the inverter efficiency, the UPS rated output power factor, the rating
          schedule and every growth, margin and derating factor before approval or issue.
        </p>
      </section>
    </main>
  );
}

export default UPSSizingPage;
